import React, { useState, useEffect, useMemo } from 'react';
import { Row, Col, Card, Button, Form, Alert, Spinner, Table, Modal, Tabs, Tab, Badge, InputGroup } from 'react-bootstrap';
import apiService from '../services/api';
import VersionInfo from './VersionInfo';

const emptyRole = { code: '', name: '', is_active: true, ordinal: 0, permissions: [] };
const emptyGroup = { code: '', accessRights: [] };
const emptyRight = { code: '', description: '', is_module_level: false, category: 'task' };

const PlatformAccessRightsDefaults = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [settingData, setSettingData] = useState(null);

    // Canonical state mirrors the JSON shape on save
    const [roles, setRoles] = useState([]);                       // [{code, name, permissions: [groupCode]}]
    const [permissionGroups, setPermissionGroups] = useState([]); // [{code, accessRights: [rightCode]}]
    const [accessRights, setAccessRights] = useState([]);         // [{code, description, is_module_level}]

    // Filters
    const [roleFilter, setRoleFilter] = useState('');
    const [groupFilter, setGroupFilter] = useState('');
    const [rightFilter, setRightFilter] = useState('');

    // Role modal
    const [showRoleModal, setShowRoleModal] = useState(false);
    const [editingRoleIndex, setEditingRoleIndex] = useState(null);
    const [roleForm, setRoleForm] = useState(emptyRole);

    // Group modal
    const [showGroupModal, setShowGroupModal] = useState(false);
    const [editingGroupIndex, setEditingGroupIndex] = useState(null);
    const [groupForm, setGroupForm] = useState(emptyGroup);

    // Right modal
    const [showRightModal, setShowRightModal] = useState(false);
    const [editingRightIndex, setEditingRightIndex] = useState(null);
    const [rightForm, setRightForm] = useState(emptyRight);

    useEffect(() => {
        loadPlatformAccessRights();
    }, []);

    const loadPlatformAccessRights = async () => {
        setLoading(true);
        setError('');

        try {
            const response = await apiService.getOrganizationDefaultSettingByCode('platform_access_rights');
            if (response.success && response.data) {
                setSettingData(response.data);
                hydrateFromValue(response.data.value || {});
            } else if (response.error && !String(response.error).toLowerCase().includes('not found')) {
                setError(response.error);
            }
        } catch (err) {
            console.error('Error loading platform access rights:', err);
            setError('Failed to load platform access rights');
        } finally {
            setLoading(false);
        }
    };

    const hydrateFromValue = (value) => {
        const incomingRoles = Array.isArray(value.roles) ? value.roles : [];
        setRoles(incomingRoles.map(r => ({
            code: r.code || '',
            name: r.name || '',
            is_active: r.is_active === undefined ? true : Boolean(r.is_active),
            ordinal: Number.isFinite(Number(r.ordinal)) ? Number(r.ordinal) : 0,
            permissions: Array.isArray(r.permissions) ? [...r.permissions] : []
        })));

        const incomingPermissions = value.permissions && typeof value.permissions === 'object' ? value.permissions : {};
        setPermissionGroups(Object.keys(incomingPermissions).map(code => ({
            code,
            accessRights: Array.isArray(incomingPermissions[code]) ? [...incomingPermissions[code]] : []
        })));

        const incomingRights = Array.isArray(value.platform_access_rights) ? value.platform_access_rights : [];
        setAccessRights(incomingRights.map(r => ({
            code: r.code || '',
            description: r.description || '',
            is_module_level: Boolean(r.is_module_level),
            category: r.category || 'task'
        })));
    };

    const buildPayloadValue = () => {
        const permissionsObj = {};
        permissionGroups.forEach(g => {
            if (g.code) permissionsObj[g.code] = [...g.accessRights];
        });
        return {
            roles: roles.map(r => ({
                code: r.code,
                name: r.name,
                is_active: Boolean(r.is_active),
                ordinal: Number.isFinite(Number(r.ordinal)) ? Number(r.ordinal) : 0,
                permissions: [...r.permissions]
            })),
            permissions: permissionsObj,
            platform_access_rights: accessRights.map(r => ({
                code: r.code,
                description: r.description,
                is_module_level: Boolean(r.is_module_level),
                category: r.category || 'task'
            }))
        };
    };

    const handleSave = async (e) => {
        if (e && e.preventDefault) e.preventDefault();
        setSaving(true);
        setError('');
        setSuccess('');

        try {
            const updateData = {
                name: 'Platform Access Rights',
                code: 'platform_access_rights',
                value: buildPayloadValue()
            };

            let response;
            if (settingData && settingData.id) {
                response = await apiService.updateOrganizationDefaultSetting(settingData.id, updateData);
            } else {
                response = await apiService.createOrganizationDefaultSetting(updateData);
            }

            if (response.success) {
                setSuccess('Platform access rights saved successfully!');
                setSettingData(response.data);
            } else {
                setError(response.error || 'Failed to save platform access rights');
            }
        } catch (err) {
            console.error('Error saving platform access rights:', err);
            setError('Failed to save platform access rights');
        } finally {
            setSaving(false);
        }
    };

    // ----- Roles -----
    const openAddRole = () => {
        setEditingRoleIndex(null);
        setRoleForm({ ...emptyRole, permissions: [] });
        setShowRoleModal(true);
    };
    const openEditRole = (index) => {
        const r = roles[index];
        setEditingRoleIndex(index);
        setRoleForm({
            code: r.code,
            name: r.name,
            is_active: r.is_active === undefined ? true : Boolean(r.is_active),
            ordinal: Number.isFinite(Number(r.ordinal)) ? Number(r.ordinal) : 0,
            permissions: [...r.permissions]
        });
        setShowRoleModal(true);
    };
    const handleRoleFieldChange = (e) => {
        const { name, value, type, checked } = e.target;
        setRoleForm(prev => ({
            ...prev,
            [name]: type === 'checkbox'
                ? checked
                : (name === 'ordinal' ? (value === '' ? '' : Number(value)) : value)
        }));
    };
    const toggleRolePermission = (groupCode) => {
        setRoleForm(prev => ({
            ...prev,
            permissions: prev.permissions.includes(groupCode)
                ? prev.permissions.filter(c => c !== groupCode)
                : [...prev.permissions, groupCode]
        }));
    };
    const handleSaveRole = (e) => {
        e.preventDefault();
        if (!roleForm.code.trim() || !roleForm.name.trim()) return;
        const next = {
            code: roleForm.code.trim(),
            name: roleForm.name.trim(),
            is_active: Boolean(roleForm.is_active),
            ordinal: Number.isFinite(Number(roleForm.ordinal)) ? Number(roleForm.ordinal) : 0,
            permissions: [...roleForm.permissions]
        };
        setRoles(prev => {
            const list = [...prev];
            if (editingRoleIndex === null) list.push(next);
            else list[editingRoleIndex] = next;
            return list;
        });
        setShowRoleModal(false);
    };
    const handleDeleteRole = (index) => {
        setRoles(prev => prev.filter((_, i) => i !== index));
    };

    // ----- Permission Groups -----
    const openAddGroup = () => {
        setEditingGroupIndex(null);
        setGroupForm({ ...emptyGroup, accessRights: [] });
        setShowGroupModal(true);
    };
    const openEditGroup = (index) => {
        const g = permissionGroups[index];
        setEditingGroupIndex(index);
        setGroupForm({ code: g.code, accessRights: [...g.accessRights] });
        setShowGroupModal(true);
    };
    const handleGroupFieldChange = (e) => {
        const { name, value } = e.target;
        setGroupForm(prev => ({ ...prev, [name]: value }));
    };
    const toggleGroupAccessRight = (rightCode) => {
        setGroupForm(prev => ({
            ...prev,
            accessRights: prev.accessRights.includes(rightCode)
                ? prev.accessRights.filter(c => c !== rightCode)
                : [...prev.accessRights, rightCode]
        }));
    };
    const handleSaveGroup = (e) => {
        e.preventDefault();
        if (!groupForm.code.trim()) return;
        const next = {
            code: groupForm.code.trim(),
            accessRights: [...groupForm.accessRights]
        };
        setPermissionGroups(prev => {
            const list = [...prev];
            if (editingGroupIndex === null) list.push(next);
            else list[editingGroupIndex] = next;
            return list;
        });
        setShowGroupModal(false);
    };
    const handleDeleteGroup = (index) => {
        setPermissionGroups(prev => prev.filter((_, i) => i !== index));
    };

    // ----- Access Rights -----
    const openAddRight = () => {
        setEditingRightIndex(null);
        setRightForm({ ...emptyRight });
        setShowRightModal(true);
    };
    const openEditRight = (index) => {
        const r = accessRights[index];
        setEditingRightIndex(index);
        setRightForm({ code: r.code, description: r.description, is_module_level: r.is_module_level, category: r.category || 'task' });
        setShowRightModal(true);
    };
    const handleRightFieldChange = (e) => {
        const { name, value, type, checked } = e.target;
        setRightForm(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };
    const handleSaveRight = (e) => {
        e.preventDefault();
        if (!rightForm.code.trim()) return;
        const next = {
            code: rightForm.code.trim(),
            description: rightForm.description.trim(),
            is_module_level: Boolean(rightForm.is_module_level),
            category: rightForm.category || 'task'
        };
        setAccessRights(prev => {
            const list = [...prev];
            if (editingRightIndex === null) list.push(next);
            else list[editingRightIndex] = next;
            return list;
        });
        setShowRightModal(false);
    };
    const handleDeleteRight = (index) => {
        setAccessRights(prev => prev.filter((_, i) => i !== index));
    };

    // ----- Derived/filtered views -----
    const filteredRoles = useMemo(() => {
        const q = roleFilter.trim().toLowerCase();
        if (!q) return roles;
        return roles.filter(r =>
            r.code.toLowerCase().includes(q) ||
            r.name.toLowerCase().includes(q)
        );
    }, [roles, roleFilter]);

    const filteredGroups = useMemo(() => {
        const q = groupFilter.trim().toLowerCase();
        if (!q) return permissionGroups;
        return permissionGroups.filter(g => g.code.toLowerCase().includes(q));
    }, [permissionGroups, groupFilter]);

    const filteredRights = useMemo(() => {
        const q = rightFilter.trim().toLowerCase();
        if (!q) return accessRights;
        return accessRights.filter(r =>
            r.code.toLowerCase().includes(q) ||
            r.description.toLowerCase().includes(q)
        );
    }, [accessRights, rightFilter]);

    const groupCodes = useMemo(() => permissionGroups.map(g => g.code).filter(Boolean), [permissionGroups]);
    const rightCodes = useMemo(() => accessRights.map(r => r.code).filter(Boolean), [accessRights]);

    if (loading) {
        return (
            <div className="text-center p-4">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
                <div className="mt-2">Loading platform access rights...</div>
            </div>
        );
    }

    return (
        <div>
            <Row>
                <Col>
                    <div className="d-flex justify-content-between align-items-center mb-4">
                        <h2>
                            <i className="bi bi-key me-2"></i>
                            Platform Access Rights Defaults
                        </h2>
                        <Button variant="primary" onClick={handleSave} disabled={saving}>
                            {saving ? (
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-check-lg me-2"></i>
                                    Save Settings
                                </>
                            )}
                        </Button>
                    </div>
                </Col>
            </Row>

            {error && (
                <Row className="mb-4">
                    <Col>
                        <Alert variant="danger" dismissible onClose={() => setError('')}>
                            <i className="bi bi-exclamation-triangle me-2"></i>
                            {error}
                        </Alert>
                    </Col>
                </Row>
            )}

            {success && (
                <Row className="mb-4">
                    <Col>
                        <Alert variant="success" dismissible onClose={() => setSuccess('')}>
                            <i className="bi bi-check-circle me-2"></i>
                            {success}
                        </Alert>
                    </Col>
                </Row>
            )}

            <Row>
                <Col lg={8}>
                    <Card>
                        <Card.Body>
                            <Tabs defaultActiveKey="roles" id="platform-access-rights-tabs" className="mb-3">
                                <Tab eventKey="roles" title={<><i className="bi bi-person-badge me-1"></i>Roles <Badge bg="secondary">{roles.length}</Badge></>}>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <InputGroup style={{ maxWidth: 320 }}>
                                            <InputGroup.Text><i className="bi bi-search"></i></InputGroup.Text>
                                            <Form.Control
                                                placeholder="Filter roles..."
                                                value={roleFilter}
                                                onChange={(e) => setRoleFilter(e.target.value)}
                                            />
                                        </InputGroup>
                                        <Button variant="primary" size="sm" onClick={openAddRole}>
                                            <i className="bi bi-plus-lg me-1"></i> Add Role
                                        </Button>
                                    </div>
                                    {filteredRoles.length === 0 ? (
                                        <div className="text-center text-muted p-4">No roles configured.</div>
                                    ) : (
                                        <Table responsive hover className="align-middle">
                                            <thead>
                                                <tr>
                                                    <th>Name</th>
                                                    <th>Code</th>
                                                    <th className="text-center">Ordinal</th>
                                                    <th className="text-center">Active</th>
                                                    <th>Permission Groups</th>
                                                    <th className="text-end">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredRoles.map((r) => {
                                                    const realIndex = roles.indexOf(r);
                                                    return (
                                                        <tr key={`${r.code}-${realIndex}`}>
                                                            <td className="fw-semibold">{r.name}</td>
                                                            <td><code>{r.code}</code></td>
                                                            <td className="text-center">{r.ordinal}</td>
                                                            <td className="text-center">
                                                                {r.is_active
                                                                    ? <Badge bg="success">Yes</Badge>
                                                                    : <Badge bg="light" text="dark">No</Badge>}
                                                            </td>
                                                            <td>
                                                                {r.permissions.length === 0 ? (
                                                                    <span className="text-muted small">None</span>
                                                                ) : (
                                                                    r.permissions.map(p => (
                                                                        <Badge key={p} bg="info" className="me-1 mb-1">{p}</Badge>
                                                                    ))
                                                                )}
                                                            </td>
                                                            <td className="text-end text-nowrap">
                                                                <Button variant="outline-secondary" size="sm" className="me-1" onClick={() => openEditRole(realIndex)}>
                                                                    <i className="bi bi-pencil"></i>
                                                                </Button>
                                                                <Button variant="outline-danger" size="sm" onClick={() => handleDeleteRole(realIndex)}>
                                                                    <i className="bi bi-trash"></i>
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                    )}
                                </Tab>

                                <Tab eventKey="groups" title={<><i className="bi bi-collection me-1"></i>Permission Groups <Badge bg="secondary">{permissionGroups.length}</Badge></>}>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <InputGroup style={{ maxWidth: 320 }}>
                                            <InputGroup.Text><i className="bi bi-search"></i></InputGroup.Text>
                                            <Form.Control
                                                placeholder="Filter groups..."
                                                value={groupFilter}
                                                onChange={(e) => setGroupFilter(e.target.value)}
                                            />
                                        </InputGroup>
                                        <Button variant="primary" size="sm" onClick={openAddGroup}>
                                            <i className="bi bi-plus-lg me-1"></i> Add Group
                                        </Button>
                                    </div>
                                    {filteredGroups.length === 0 ? (
                                        <div className="text-center text-muted p-4">No permission groups configured.</div>
                                    ) : (
                                        <Table responsive hover className="align-middle">
                                            <thead>
                                                <tr>
                                                    <th>Group Code</th>
                                                    <th>Access Rights</th>
                                                    <th className="text-end">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredGroups.map((g) => {
                                                    const realIndex = permissionGroups.indexOf(g);
                                                    return (
                                                        <tr key={`${g.code}-${realIndex}`}>
                                                            <td><code>{g.code}</code></td>
                                                            <td>
                                                                {g.accessRights.length === 0 ? (
                                                                    <span className="text-muted small">None</span>
                                                                ) : (
                                                                    <span className="small">
                                                                        <Badge bg="primary" className="me-2">{g.accessRights.length}</Badge>
                                                                        {g.accessRights.slice(0, 4).join(', ')}
                                                                        {g.accessRights.length > 4 && ` +${g.accessRights.length - 4} more`}
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td className="text-end text-nowrap">
                                                                <Button variant="outline-secondary" size="sm" className="me-1" onClick={() => openEditGroup(realIndex)}>
                                                                    <i className="bi bi-pencil"></i>
                                                                </Button>
                                                                <Button variant="outline-danger" size="sm" onClick={() => handleDeleteGroup(realIndex)}>
                                                                    <i className="bi bi-trash"></i>
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                    )}
                                </Tab>

                                <Tab eventKey="rights" title={<><i className="bi bi-shield-check me-1"></i>Access Rights <Badge bg="secondary">{accessRights.length}</Badge></>}>
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <InputGroup style={{ maxWidth: 320 }}>
                                            <InputGroup.Text><i className="bi bi-search"></i></InputGroup.Text>
                                            <Form.Control
                                                placeholder="Filter access rights..."
                                                value={rightFilter}
                                                onChange={(e) => setRightFilter(e.target.value)}
                                            />
                                        </InputGroup>
                                        <Button variant="primary" size="sm" onClick={openAddRight}>
                                            <i className="bi bi-plus-lg me-1"></i> Add Access Right
                                        </Button>
                                    </div>
                                    {filteredRights.length === 0 ? (
                                        <div className="text-center text-muted p-4">No access rights configured.</div>
                                    ) : (
                                        <Table responsive hover className="align-middle">
                                            <thead>
                                                <tr>
                                                    <th>Code</th>
                                                    <th>Description</th>
                                                    <th className="text-center">Module Level</th>
                                                    <th className="text-center">Category</th>
                                                    <th className="text-end">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {filteredRights.map((r) => {
                                                    const realIndex = accessRights.indexOf(r);
                                                    return (
                                                        <tr key={`${r.code}-${realIndex}`}>
                                                            <td><code>{r.code}</code></td>
                                                            <td>{r.description}</td>
                                                            <td className="text-center">
                                                                {r.is_module_level
                                                                    ? <Badge bg="success">Yes</Badge>
                                                                    : <Badge bg="light" text="dark">No</Badge>}
                                                            </td>
                                                            <td className="text-center">
                                                                <Badge bg="info" className="text-uppercase">{r.category || 'task'}</Badge>
                                                            </td>
                                                            <td className="text-end text-nowrap">
                                                                <Button variant="outline-secondary" size="sm" className="me-1" onClick={() => openEditRight(realIndex)}>
                                                                    <i className="bi bi-pencil"></i>
                                                                </Button>
                                                                <Button variant="outline-danger" size="sm" onClick={() => handleDeleteRight(realIndex)}>
                                                                    <i className="bi bi-trash"></i>
                                                                </Button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </Table>
                                    )}
                                </Tab>
                            </Tabs>
                        </Card.Body>
                    </Card>
                </Col>

                <Col lg={4}>
                    <Card className="bg-light mb-3">
                        <Card.Header>
                            <h6 className="mb-0">
                                <i className="bi bi-info-circle me-2"></i>
                                About Platform Access Rights
                            </h6>
                        </Card.Header>
                        <Card.Body>
                            <p className="mb-2">
                                <strong>Roles:</strong> Named bundles assigned to users (e.g. <em>Coordinator</em>) that map to one or more permission groups.
                            </p>
                            <p className="mb-2">
                                <strong>Permission Groups:</strong> Logical groupings (e.g. <code>v_reporting_rights</code>) that bundle individual access rights.
                            </p>
                            <p className="mb-0">
                                <strong>Access Rights:</strong> The atomic permissions (e.g. <code>_SIM_SALES_RECORD_</code>) granted to features. Mark <em>Module Level</em> for top-level module toggles.
                            </p>
                        </Card.Body>
                    </Card>

                    <Card className="mb-3">
                        <Card.Header>
                            <h6 className="mb-0">
                                <i className="bi bi-bar-chart me-2"></i>
                                Summary
                            </h6>
                        </Card.Header>
                        <Card.Body>
                            <div className="d-flex justify-content-between mb-2">
                                <span>Roles</span>
                                <Badge bg="primary">{roles.length}</Badge>
                            </div>
                            <div className="d-flex justify-content-between mb-2">
                                <span>Permission Groups</span>
                                <Badge bg="primary">{permissionGroups.length}</Badge>
                            </div>
                            <div className="d-flex justify-content-between">
                                <span>Access Rights</span>
                                <Badge bg="primary">{accessRights.length}</Badge>
                            </div>
                        </Card.Body>
                    </Card>

                    <VersionInfo />
                </Col>
            </Row>

            {/* Role Modal */}
            <Modal show={showRoleModal} onHide={() => setShowRoleModal(false)} size="lg" backdrop="static">
                <Form onSubmit={handleSaveRole}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <i className="bi bi-person-badge me-2"></i>
                            {editingRoleIndex === null ? 'Add Role' : 'Edit Role'}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Name <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        value={roleForm.name}
                                        onChange={handleRoleFieldChange}
                                        placeholder="e.g. Coordinator"
                                        required
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Code <span className="text-danger">*</span></Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="code"
                                        value={roleForm.code}
                                        onChange={handleRoleFieldChange}
                                        placeholder="e.g. _PLATFORM_ROLE_COORDINATOR_"
                                        required
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Ordinal</Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="ordinal"
                                        value={roleForm.ordinal}
                                        onChange={handleRoleFieldChange}
                                        placeholder="0"
                                    />
                                    <Form.Text className="text-muted">
                                        Display/sort order for this role.
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3 mt-md-4 pt-md-2">
                                    <Form.Check
                                        type="checkbox"
                                        id="role-is-active"
                                        name="is_active"
                                        checked={Boolean(roleForm.is_active)}
                                        onChange={handleRoleFieldChange}
                                        label="Is Active"
                                    />
                                    <Form.Text className="text-muted">
                                        Inactive roles are retained but not assignable.
                                    </Form.Text>
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Label className="mb-2">Permission Groups</Form.Label>
                        {groupCodes.length === 0 ? (
                            <div className="text-muted small">
                                No permission groups defined yet. Add some on the Permission Groups tab first.
                            </div>
                        ) : (
                            <div className="border rounded p-2" style={{ maxHeight: 300, overflowY: 'auto' }}>
                                {groupCodes.map(code => (
                                    <Form.Check
                                        key={code}
                                        type="checkbox"
                                        id={`role-perm-${code}`}
                                        label={<code>{code}</code>}
                                        checked={roleForm.permissions.includes(code)}
                                        onChange={() => toggleRolePermission(code)}
                                    />
                                ))}
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" type="button" onClick={() => setShowRoleModal(false)}>Cancel</Button>
                        <Button variant="primary" type="submit">
                            <i className="bi bi-check-lg me-2"></i>
                            {editingRoleIndex === null ? 'Add Role' : 'Save Changes'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Group Modal */}
            <Modal show={showGroupModal} onHide={() => setShowGroupModal(false)} size="lg" backdrop="static">
                <Form onSubmit={handleSaveGroup}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <i className="bi bi-collection me-2"></i>
                            {editingGroupIndex === null ? 'Add Permission Group' : 'Edit Permission Group'}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Group Code <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text"
                                name="code"
                                value={groupForm.code}
                                onChange={handleGroupFieldChange}
                                placeholder="e.g. v_reporting_rights"
                                required
                            />
                        </Form.Group>
                        <Form.Label className="mb-2">Access Rights</Form.Label>
                        {rightCodes.length === 0 ? (
                            <div className="text-muted small">
                                No access rights defined yet. Add some on the Access Rights tab first.
                            </div>
                        ) : (
                            <div className="border rounded p-2" style={{ maxHeight: 300, overflowY: 'auto' }}>
                                {rightCodes.map(code => (
                                    <Form.Check
                                        key={code}
                                        type="checkbox"
                                        id={`group-right-${code}`}
                                        label={<code>{code}</code>}
                                        checked={groupForm.accessRights.includes(code)}
                                        onChange={() => toggleGroupAccessRight(code)}
                                    />
                                ))}
                            </div>
                        )}
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" type="button" onClick={() => setShowGroupModal(false)}>Cancel</Button>
                        <Button variant="primary" type="submit">
                            <i className="bi bi-check-lg me-2"></i>
                            {editingGroupIndex === null ? 'Add Group' : 'Save Changes'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Right Modal */}
            <Modal show={showRightModal} onHide={() => setShowRightModal(false)} backdrop="static">
                <Form onSubmit={handleSaveRight}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <i className="bi bi-shield-check me-2"></i>
                            {editingRightIndex === null ? 'Add Access Right' : 'Edit Access Right'}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label>Code <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                type="text"
                                name="code"
                                value={rightForm.code}
                                onChange={handleRightFieldChange}
                                placeholder="e.g. _SIM_SALES_RECORD_"
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Description</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                name="description"
                                value={rightForm.description}
                                onChange={handleRightFieldChange}
                                placeholder="Human-readable description"
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Category <span className="text-danger">*</span></Form.Label>
                            <Form.Select
                                name="category"
                                value={rightForm.category || 'task'}
                                onChange={handleRightFieldChange}
                            >
                                <option value="module">Module</option>
                                <option value="section">Section</option>
                                <option value="task">Task</option>
                            </Form.Select>
                            <Form.Text className="text-muted">
                                Classifies the access right as a module, section, or task.
                            </Form.Text>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                id="right-is-module-level"
                                name="is_module_level"
                                checked={rightForm.is_module_level}
                                onChange={handleRightFieldChange}
                                label="Is Module Level"
                            />
                            <Form.Text className="text-muted">
                                Module-level rights act as top-level toggles for an entire module.
                            </Form.Text>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" type="button" onClick={() => setShowRightModal(false)}>Cancel</Button>
                        <Button variant="primary" type="submit">
                            <i className="bi bi-check-lg me-2"></i>
                            {editingRightIndex === null ? 'Add Access Right' : 'Save Changes'}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </div>
    );
};

export default PlatformAccessRightsDefaults;
